import { useEffect, useState } from "react"

import { BuildingReferenceObj } from "scrivito/Objs/BuildingReference/BuildingReferenceObjClass"
import { DEFAULT_FUZZY_SEARCH_OPTIONS } from "shared/constants/search"
import { useFuzzySearchWithRanges } from "shared/lib/useFuzzySearchWithRanges"

import { BuildingReferenceSearchResult } from "../types/GlobalSearch"
import { fetchBuildingReferencesByTitle } from "./fetchBuildingReferencesByTitle"

interface UseBuildingReferencesByTitleProps {
  searchTerm?: string | null
}

function getBuildingReferenceTitle(obj: BuildingReferenceObj) {
  return obj.get("title") ?? ""
}

export function useBuildingReferencesByTitle({
  searchTerm
}: UseBuildingReferencesByTitleProps): {
  results: BuildingReferenceSearchResult[]
  isLoading: boolean
} {
  const [results, setResults] = useState<BuildingReferenceSearchResult[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const fuzzySearch = useFuzzySearchWithRanges<BuildingReferenceObj>({
    getSearchableString: getBuildingReferenceTitle,
    options: DEFAULT_FUZZY_SEARCH_OPTIONS
  })

  useEffect(() => {
    if (!searchTerm || searchTerm.length < 2) {
      setResults([])
      setIsLoading(false)
      return undefined
    }

    let isCancelled = false
    setIsLoading(true)

    fetchBuildingReferencesByTitle(searchTerm)
      .then(objs => {
        if (!isCancelled) {
          const fuzzyResults = fuzzySearch(objs, searchTerm)
          setResults(
            fuzzyResults.map(({ item: obj, ranges }) => ({
              obj,
              id: obj.id(),
              title: obj.get("title"),
              matchInfo: { title: { searchTerm, ranges } }
            }))
          )
        }
      })
      .catch(() => {
        if (!isCancelled) setResults([])
      })
      .finally(() => {
        if (!isCancelled) setIsLoading(false)
      })

    return () => {
      isCancelled = true
    }
  }, [searchTerm, fuzzySearch])

  return { results, isLoading }
}
